<template>
  <div class="password-reset-page">
  <BaseContainer backgroundColor="white" :col-md="6">
    <Header
      v-if="!isLoading"
      :title="title"
      :subtitle="subtitle"
      :foregroundColor="foregroundColor"
      imageSize="small"
    />
    <Loader
      v-if="isLoading"
      class="overlay"
      :text="loadingText"
      :textColor="foregroundColor"
      :spinnerColor="foregroundColor"
    />
    <v-container v-else-if="isCodeValid && !isSuccess && !isError">
      <v-form @submit.prevent="handleSubmit">
        <v-text-field
          v-model="password"
          label="Enter new password"
          :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="showPassword = !showPassword"
          :type="showPassword ? 'text' : 'password'"
          required
          class="mb-4"
          :style="{ color: foregroundColor }"
        ></v-text-field>
        <v-btn
          type="submit"
          size="large"
          class="submit-btn"
        >
          Set new password
        </v-btn>
      </v-form>
    </v-container>
  </BaseContainer>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { verifyCode, resetPassword, mapAuthError } from '../../firebase-authentication.js';
import { track } from '../../analytics.js';
import BaseContainer from "@/components/Atoms/BaseContainer.vue";
import Loader from "@/components/Molecules/BrandedLoader.vue";
import Header from "@/components/Molecules/BrandedHeader.vue";

export default {
  components: {
    BaseContainer,
    Loader,
    Header,
  },
  setup() {
    const title = ref("");
    const subtitle = ref("");
    const email = ref("");
    const password = ref("");
    const isLoading = ref(false);
    const isSuccess = ref(false);
    const isError = ref(false);
    const loadingText = ref("Resetting password...");
    const isCodeValid = ref(false);
    const foregroundColor = ref("rgb(0, 109, 62)");
    const backgroundColor = ref("rgb(255,255,255)");
    const showPassword = ref(false);

    const urlParams = new URLSearchParams(window.location.search)
    const oobCode = urlParams.get("oobCode")

    onMounted(async () => {
      const result = await verifyCode(oobCode)
      if (result.success) {
        title.value = "Password reset";
        email.value = result.email
        isCodeValid.value = true
      } else {
        const mapped = mapAuthError(result.error);
        track("password_reset_link_invalid", { error_code: result.error?.code });
        title.value = "This reset link is invalid or has expired";
        subtitle.value = mapped.message;
        isError.value = true;
      }
    });

    const handleSubmit = async () => {
      isError.value = false;
      isLoading.value = true;
      title.value = "";
      subtitle.value = "";
      isSuccess.value = false
      const result = await resetPassword(oobCode, password.value)
      isLoading.value = false

      if (result.success) {
        track("password_reset_completed", {});
        subtitle.value = "Your password has been successfully reset ✅";
        isSuccess.value = true;
      } else {
        const mapped = mapAuthError(result.error);
        track("password_reset_failed", { error_message: mapped.message, stage: "complete" });
        title.value = "Password reset failed";
        subtitle.value = mapped.message;
        isError.value = true;
      }
    };

    return {
      title,
      subtitle,
      password,
      isLoading,
      isSuccess,
      isError,
      loadingText,
      isCodeValid,
      foregroundColor,
      backgroundColor,
      showPassword,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.password-reset-page {
  background: linear-gradient(160deg, #f0faf5 0%, #e6f4ee 100%);
  min-height: 100vh;
}

input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}

.submit-btn {
  background: rgb(0, 109, 62) !important;
  color: white !important;
  padding: 12px 28px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  text-transform: none !important;
  margin-top: 8px !important;
  margin-bottom: 20px !important;
  display: block;
  margin-left: auto !important;
  margin-right: auto !important;
}

.submit-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
}

.text-container {
  width: 100%;
  text-align: center;
  margin-bottom: 24px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  display: block;
  margin-bottom: 16px;
}

.subtitle {
  font-size: 14px;
  font-weight: normal;
  display: block;
}
.loading-text {
  margin-top: 10px;
}
</style>
